import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  RuleField,
  RuleOperator,
  StorageContainer,
  Tag,
  TaggingRule,
  TaggingRuleCondition,
  TaggingRuleTestResult,
} from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { NestFilterBanner } from '../components/NestFilterBanner.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { useListSearch } from '../lib/use-list-search.js';
import { ListSearchInput } from '../components/ListSearchInput.js';
import { EditedTimestampStrip } from '../components/EditedTimestampStrip.js';
// ctsk-070 round-2 — Lucide icons replace the emoji glyphs on the
// action buttons so the rules table reads with the same visual
// language as the rest of the app (RowActions ⋯, MimeIcon, etc.).
// See components/MimeIcon.tsx for the @ts-ignore rationale.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Play, Zap, Pencil, Trash2 } from 'lucide-react';

const FIELD_OPTIONS: { value: RuleField; label: string }[] = [
  { value: 'title', label: 'Title' },
  { value: 'document_name', label: 'Filename (original upload)' },
  { value: 'content', label: 'Content' },
  { value: 'file_type', label: 'File type (MIME)' },
  // 0.1.81 (CTSK-140 Stage 4b) — resolved baseline type id (e.g.
  // `markdown-document`, `html-document`, `crawl-page`). Prefer this
  // over `file_type` when the intent is "any markdown regardless of
  // extension" or "any HTML regardless of whether it came from a
  // .html upload or a Clipper paste."
  { value: 'baseline_type', label: 'Baseline document type' },
  { value: 'date', label: 'Date' },
  { value: 'metadata_field', label: 'Frontmatter field' },
  { value: 'custom_property', label: 'Custom property' },
];

const OPERATOR_OPTIONS: { value: RuleOperator; label: string }[] = [
  { value: 'contains', label: 'contains' },
  { value: 'equals', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'regex_match', label: 'matches regex' },
  { value: 'before', label: 'before (date)' },
  { value: 'after', label: 'after (date)' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

export function TaggingRulesAdmin() {
  const qc = useQueryClient();
  const rules = useQuery({
    queryKey: ['tagging-rules'],
    queryFn: () => apiList<TaggingRule>('/tagging-rules'),
  });
  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiList<Tag>('/tags'),
  });
  const containersQ = useQuery({
    queryKey: ['storage-containers'],
    queryFn: () => apiList<StorageContainer>('/storage-containers'),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [testFor, setTestFor] = useState<string | null>(null);

  // ?nest=<slug> deep-link from the sidebar Rules stat. Same v1
  // tag-prefix scan as the bundle export filter: keep rules whose
  // tagsToApply includes the master tag `nest/<slug>`'s id.
  const [nestSlug, setNestSlug] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('nest');
  });

  const nestFilteredRules = useMemo(() => {
    const all = rules.data?.data ?? [];
    if (!nestSlug) return all;
    const masterTag = (tagsQ.data?.data ?? []).find((t) => t.name === `nest/${nestSlug}`);
    if (!masterTag) return []; // nest has no master tag yet → no matches
    return all.filter((r) => r.tagsToApply.includes(masterTag.id));
  }, [rules.data?.data, tagsQ.data?.data, nestSlug]);

  // FR #184 — top-of-list search on name + description. Runs after
  // nest filtering so the row count in the page subtitle reflects
  // the nest selection, while the "Showing N of M" hint reflects
  // the search narrowing within that scope.
  const search = useListSearch(nestFilteredRules, {
    fields: [(r) => r.name, (r) => r.description ?? ''],
    urlParam: 'q',
  });

  const { sorted, sort, setSort } = useTableSort(search.filtered, {
    priority: (r: TaggingRule) => r.priority,
    name: (r) => r.name,
    matchLogic: (r) => r.matchLogic,
    conditions: (r) => r.conditions.length,
    tagsToApply: (r) => r.tagsToApply.length,
    tagsToRemove: (r) => r.tagsToRemove.length,
    enabled: (r) => r.enabled,
    matchedCount: (r) => r.matchedCount,
  }, { key: 'priority', dir: 'desc' });

  const del = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/tagging-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Rule deleted'); qc.invalidateQueries({ queryKey: ['tagging-rules'] }); },
  });

  const run = useMutation({
    mutationFn: (id: string) => apiSingle<{ matchedDocs: number; tagOperations: number; containerOperations: number }>(
      `/tagging-rules/${id}/run`,
      { method: 'POST' },
    ),
    onSuccess: (data) => {
      const containerNote = data.containerOperations > 0 ? `, ${data.containerOperations} container assignments` : '';
      toast.success(`Ran rule: matched ${data.matchedDocs} docs, ${data.tagOperations} tag changes${containerNote}`);
      qc.invalidateQueries({ queryKey: ['tagging-rules'] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['storage-containers'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tagging rules</h1>
          <div className="page-subtitle">
            {nestSlug
              ? `${nestFilteredRules.length} rule${nestFilteredRules.length === 1 ? '' : 's'} in this nest · higher priority runs first`
              : `${rules.data?.data.length ?? 0} rules · higher priority runs first`}
          </div>
        </div>
        <button className="btn btn--create" onClick={() => setShowCreate(true)}>+ New rule</button>
      </div>

      {nestSlug && (
        <NestFilterBanner
          slug={nestSlug}
          onClear={() => {
            setNestSlug(null);
            // Strip ?nest= from the URL so a refresh doesn't re-apply.
            if (typeof window !== 'undefined') {
              const sp = new URLSearchParams(window.location.search);
              sp.delete('nest');
              const qs = sp.toString();
              const next = window.location.pathname + (qs ? `?${qs}` : '');
              window.history.replaceState(null, '', next);
            }
          }}
        />
      )}

      {rules.data && rules.data.data.length > 0 && (
        <ListSearchInput
          value={search.query}
          onChange={search.setQuery}
          placeholder="Search rules by name or description…"
          totalCount={nestFilteredRules.length}
          filteredCount={search.filtered.length}
        />
      )}

      {rules.isLoading && <SkeletonRows rows={4} />}
      {rules.data && rules.data.data.length === 0 && (
        <EmptyState
          title="No tagging rules"
          body="Tagging rules automatically apply tags to documents based on filename, content, or metadata."
          action={<button className="btn btn--create" onClick={() => setShowCreate(true)}>+ New rule</button>}
        />
      )}
      {rules.data && rules.data.data.length > 0 && search.isActive && search.filtered.length === 0 && (
        <EmptyState
          title="No rules match your search"
          body={`Nothing matches "${search.query}". Try a shorter query or clear the search.`}
          action={<button className="btn" onClick={() => search.setQuery('')}>Clear search</button>}
        />
      )}
      {rules.data && rules.data.data.length > 0 && search.filtered.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <SortableTh sortKey="priority" sort={sort} onSortChange={setSort}>Priority</SortableTh>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
              <SortableTh sortKey="matchLogic" sort={sort} onSortChange={setSort}>Match</SortableTh>
              <SortableTh sortKey="conditions" sort={sort} onSortChange={setSort}>Conditions</SortableTh>
              <SortableTh sortKey="tagsToApply" sort={sort} onSortChange={setSort}>Apply</SortableTh>
              <SortableTh sortKey="tagsToRemove" sort={sort} onSortChange={setSort}>Remove</SortableTh>
              <th>Triggers</th>
              <SortableTh sortKey="enabled" sort={sort} onSortChange={setSort}>Status</SortableTh>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id}>
                <td>{r.priority}</td>
                <td>
                  <strong>{r.name}</strong>
                  {r.description && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.description}</div>}
                  {r.lastRunAt && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Last run: {new Date(r.lastRunAt).toLocaleString()} · {r.matchedCount} total matches</div>}
                </td>
                <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.matchLogic}</code></td>
                <td>{r.conditions.length}</td>
                <td>{r.tagsToApply.length}</td>
                <td>{r.tagsToRemove.length}</td>
                <td style={{ fontSize: 12 }}>{r.triggers.join(', ')}</td>
                {/* ctsk-070 — color-coded pill instead of plain text so
                    enabled/disabled stands out at a glance. */}
                <td><RuleStatusPill enabled={r.enabled} /></td>
                {/* ctsk-070 — actions get a button-group treatment: Test
                    + Run cluster as "execute" actions, Edit + Delete
                    cluster as "manage" actions, with a small gap between
                    the two clusters. Delete is destructive-tinted. */}
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
                      <button
                        className="btn btn--ghost"
                        onClick={() => setTestFor(r.id)}
                        title="Dry-run the rule against current documents — see what would match without applying."
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Play size={13} strokeWidth={1.75} aria-hidden /> Test
                      </button>
                      <button
                        className="btn btn--ghost"
                        onClick={() => run.mutate(r.id)}
                        title="Apply the rule to every matching document now."
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Zap size={13} strokeWidth={1.75} aria-hidden /> Run
                      </button>
                    </span>
                    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
                      <button
                        className="btn btn--ghost"
                        onClick={() => setEditingRuleId(r.id)}
                        title="Edit conditions, actions, and priority."
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Pencil size={13} strokeWidth={1.75} aria-hidden /> Edit
                      </button>
                      <button
                        className="btn btn--ghost"
                        onClick={() => { if (confirm(`Delete rule "${r.name}"?`)) del.mutate(r.id); }}
                        title="Delete this rule."
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-primary)' }}
                      >
                        <Trash2 size={13} strokeWidth={1.75} aria-hidden /> Delete
                      </button>
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(showCreate || editingRuleId) && (
        <RuleDialog
          tags={tagsQ.data?.data ?? []}
          containers={containersQ.data?.data ?? []}
          rule={editingRuleId ? rules.data?.data.find((r) => r.id === editingRuleId) ?? null : null}
          onCancel={() => { setShowCreate(false); setEditingRuleId(null); }}
          onSaved={() => {
            setShowCreate(false);
            setEditingRuleId(null);
            qc.invalidateQueries({ queryKey: ['tagging-rules'] });
          }}
        />
      )}
      {testFor && (
        <TestResultsDialog ruleId={testFor} onClose={() => setTestFor(null)} />
      )}
    </>
  );
}

function RuleDialog({
  tags,
  containers,
  rule,
  onCancel,
  onSaved,
}: {
  tags: Tag[];
  containers: StorageContainer[];
  rule: TaggingRule | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [matchLogic, setMatchLogic] = useState<'all' | 'any'>(rule?.matchLogic ?? 'all');
  const [conditions, setConditions] = useState<TaggingRuleCondition[]>(
    rule?.conditions ?? [{ field: 'title', operator: 'contains', value: '' }],
  );
  const [tagsToApply, setTagsToApply] = useState<string[]>(rule?.tagsToApply ?? []);
  const [tagsToRemove, setTagsToRemove] = useState<string[]>(rule?.tagsToRemove ?? []);
  const [containerToAssign, setContainerToAssign] = useState<string | null>(rule?.containerToAssign ?? null);
  const [priority, setPriority] = useState(rule?.priority ?? 10);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        matchLogic,
        conditions,
        tagsToApply,
        tagsToRemove,
        containerToAssign,           // null when "— None —" picked; clears on PATCH
        priority,
        enabled,
      };
      return rule
        ? apiSingle<TaggingRule>(`/tagging-rules/${rule.id}`, { method: 'PATCH', body })
        : apiSingle<TaggingRule>('/tagging-rules', { method: 'POST', body });
    },
    onSuccess: () => { toast.success('Rule saved'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  function setCondition(i: number, next: Partial<TaggingRuleCondition>) {
    setConditions((arr) => arr.map((c, idx) => (idx === i ? { ...c, ...next } : c)));
  }
  function addCondition() {
    setConditions((arr) => [...arr, { field: 'title', operator: 'contains', value: '' }]);
  }
  function removeCondition(i: number) {
    setConditions((arr) => arr.filter((_, idx) => idx !== i));
  }

  return (
    <div role="dialog" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 720, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ margin: 0, marginBottom: 16 }}>{rule ? `Edit rule: ${rule.name}` : 'New tagging rule'}</h2>
        {rule && <EditedTimestampStrip createdAt={rule.createdAt} updatedAt={rule.updatedAt} />}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12 }}>
          <div className="field">
            <label htmlFor="r-name">Name</label>
            <input id="r-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div className="field">
            <label htmlFor="r-pri">Priority</label>
            <input id="r-pri" type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="r-desc">Description</label>
          <input id="r-desc" value={description ?? ''} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Auto-tag invoices from billing@" />
        </div>

        <div className="field">
          <label>Match logic</label>
          <div style={{ display: 'flex', gap: 16 }}>
            <label><input type="radio" name="match" checked={matchLogic === 'all'} onChange={() => setMatchLogic('all')} /> All conditions must match</label>
            <label><input type="radio" name="match" checked={matchLogic === 'any'} onChange={() => setMatchLogic('any')} /> Any condition may match</label>
          </div>
        </div>

        <div className="field">
          <label>Conditions</label>
          {conditions.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <select value={c.field} onChange={(e) => setCondition(i, { field: e.target.value as RuleField })}>
                {FIELD_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              {(c.field === 'metadata_field' || c.field === 'custom_property') && (
                <input value={c.fieldKey ?? ''} onChange={(e) => setCondition(i, { fieldKey: e.target.value })} placeholder="field key" style={{ width: 120 }} />
              )}
              <select value={c.operator} onChange={(e) => setCondition(i, { operator: e.target.value as RuleOperator })}>
                {OPERATOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {!['is_empty', 'is_not_empty'].includes(c.operator) && (
                <input value={c.value ?? ''} onChange={(e) => setCondition(i, { value: e.target.value })} placeholder="value" style={{ flex: 1 }} />
              )}
              <button className="btn btn--ghost" onClick={() => removeCondition(i)} disabled={conditions.length === 1}>×</button>
            </div>
          ))}
          <button className="btn" onClick={addCondition}>+ Add condition</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Apply tags</label>
            <TagMultiSelect tags={tags} selected={tagsToApply} onChange={setTagsToApply} />
          </div>
          <div className="field">
            <label>Remove tags</label>
            <TagMultiSelect tags={tags} selected={tagsToRemove} onChange={setTagsToRemove} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="r-container">Place in storage container</label>
          <select
            id="r-container"
            value={containerToAssign ?? ''}
            onChange={(e) => setContainerToAssign(e.target.value || null)}
            style={{ width: '100%' }}
          >
            <option value="">— None —</option>
            {containers
              .filter((c) => !c.deletedAt)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.isDefault ? ' (default)' : ''}
                </option>
              ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            When the rule matches, the document is added to this container alongside its existing ones.
          </div>
        </div>

        <div className="field">
          <label><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled</label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary" disabled={!name || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : (rule ? 'Save changes' : 'Create rule')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TagMultiSelect({
  tags,
  selected,
  onChange,
}: {
  tags: Tag[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 4, padding: 6, maxHeight: 120, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tags.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No tags exist yet</span>}
      {tags.map((t) => {
        const checked = selected.includes(t.id);
        return (
          <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(e.target.checked ? [...selected, t.id] : selected.filter((id) => id !== t.id))}
            />
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: t.color }} />
            {t.name}
          </label>
        );
      })}
    </div>
  );
}

function TestResultsDialog({ ruleId, onClose }: { ruleId: string; onClose: () => void }) {
  const test = useQuery({
    queryKey: ['tagging-rule-test', ruleId],
    queryFn: () =>
      apiSingle<TaggingRuleTestResult>(`/tagging-rules/${ruleId}/test`, {
        method: 'POST',
        body: { sampleSize: 50 },
      }),
  });

  return (
    <div role="dialog" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2100 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 640, maxHeight: '85vh', overflow: 'auto' }}>
        <h2 style={{ margin: 0, marginBottom: 12 }}>Test results</h2>
        {test.isLoading && <SkeletonRows rows={5} />}
        {test.data && (
          <>
            <p style={{ color: 'var(--text-muted)' }}>
              Matched <strong>{test.data.matchCount}</strong> documents. Showing first {test.data.samples.length}.
            </p>
            <table className="grid">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Would add</th>
                  <th>Would remove</th>
                </tr>
              </thead>
              <tbody>
                {test.data.samples.map((s) => (
                  <tr key={s.documentId}>
                    <td>{s.title}</td>
                    <td>{s.wouldAdd.length}</td>
                    <td>{s.wouldRemove.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn btn--primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}


// ctsk-070 — color-coded pill for the tagging-rule Status column.
// Enabled = green; disabled = muted grey. Keeps the column scannable
// at a glance instead of needing to read every row's text.
function RuleStatusPill({ enabled }: { enabled: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        background: enabled
          ? 'color-mix(in srgb, var(--success-primary) 14%, var(--surface-raised))'
          : 'var(--surface-sunken)',
        color: enabled ? 'var(--success-primary)' : 'var(--text-muted)',
        border: enabled
          ? '1px solid color-mix(in srgb, var(--success-primary) 40%, transparent)'
          : '1px solid var(--border-subtle)',
      }}
      title={enabled ? 'Active — this rule runs on matching uploads' : 'Disabled — not running'}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: enabled ? 'var(--success-primary)' : 'var(--text-muted)',
          display: 'inline-block',
        }}
      />
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}
